import type { GpSurfacesMapSamplerCoverageDepartment } from '../types/gp-surfaces-map';

import {
  INCOMPLETE_SAMPLER_COVERAGE_COLOR,
  samplerOrganizationColor,
  SamplerOrganizationColorMap,
} from './sampler-coverage-map';

export type SamplerCoverageStripeSegment = {
  color: string;
  percent: number;
};

export type SamplerCoverageDeptPattern = {
  id: string;
  segments: SamplerCoverageStripeSegment[];
};

export type SamplerCoverageSvgFillPlan = {
  fillsByCode: Map<string, string>;
  patterns: SamplerCoverageDeptPattern[];
};

function patternIdForDepartment(code: string): string {
  return `sampler-coverage-dept-${code.replace(/\W/g, '_')}`;
}

function segmentsForDepartment(
  department: GpSurfacesMapSamplerCoverageDepartment,
  colorMap: SamplerOrganizationColorMap,
): SamplerCoverageStripeSegment[] {
  const segments: SamplerCoverageStripeSegment[] = department.shares.map(
    (share) => ({
      color: samplerOrganizationColor(
        share.sampler_organization_id,
        colorMap,
      ),
      percent: share.share_percent,
    }),
  );

  if (department.uncovered_share_percent > 0) {
    segments.push({
      color: INCOMPLETE_SAMPLER_COVERAGE_COLOR,
      percent: department.uncovered_share_percent,
    });
  }

  if (segments.length === 0) {
    return [{ color: INCOMPLETE_SAMPLER_COVERAGE_COLOR, percent: 100 }];
  }

  return segments;
}

function shouldUseStripePattern(segments: SamplerCoverageStripeSegment[]): boolean {
  return segments.length > 1;
}

export function buildSamplerCoverageSvgFillPlan(
  departments: GpSurfacesMapSamplerCoverageDepartment[],
  colorMap: SamplerOrganizationColorMap,
): SamplerCoverageSvgFillPlan {
  const fillsByCode = new Map<string, string>();
  const patterns: SamplerCoverageDeptPattern[] = [];

  for (const department of departments) {
    const segments = segmentsForDepartment(department, colorMap);
    const id = patternIdForDepartment(department.code);

    if (!shouldUseStripePattern(segments)) {
      fillsByCode.set(department.code, segments[0].color);
      continue;
    }

    patterns.push({ id, segments });
    fillsByCode.set(department.code, `url(#${id})`);
  }

  return { fillsByCode, patterns };
}
